<template>
  <template v-if="count">
    <div
      class="mb-4"
      v-if="markdownMetadata.length"
    >
      <MarkdownMetadata
        v-for="metadata in markdownMetadata"
        :key="metadata.id"
        :corpus-id="corpusId"
        :element-id="elementId"
        :metadata="metadata"
      />
    </div>
    <table
      class="table is-striped is-fullwidth mb-2"
      v-if="standardMetadata.length"
    >
      <tbody>
        <StandardMetadata
          v-for="metadata in standardMetadata"
          :key="metadata.id"
          :corpus-id="corpusId"
          :element-id="elementId"
          :metadata="metadata"
        />
      </tbody>
    </table>
  </template>
  <div class="has-text-right">
    <a
      v-if="canWrite(corpus)"
      v-on:click="editModal = true"
    >
      <button class="button is-primary">
        <i class="icon-plus"></i>
      </button>
    </a>
  </div>
  <EditModal
    v-model="editModal"
    :corpus-id="corpusId"
    :element-id="elementId"
  />
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import { METADATA_TYPES } from '@/config'
import { corporaMixin } from '@/mixins'
import { useDisplayStore, useMetaDataStore } from '@/stores'
import type { UUID } from '@/types'

import EditModal from './EditModal.vue'
import MarkdownMetadata from './Markdown.vue'
import StandardMetadata from './Metadata.vue'

/**
 * Displays all metadata for an element in the details panel.
 */
export default defineComponent({
  mixins: [corporaMixin],
  components: {
    EditModal,
    MarkdownMetadata,
    StandardMetadata,
  },
  props: {
    corpusId: {
      type: String as PropType<UUID>,
      required: true,
    },
    elementId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    METADATA_TYPES,
    editModal: false,
  }),
  computed: {
    ...mapStores(useDisplayStore, useMetaDataStore),
    elementMetadata() {
      return [...(this.metadataStore.metadata[this.elementId] ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      )
    },
    count() {
      return this.elementMetadata?.length
    },
    markdownMetadata() {
      return this.elementMetadata.filter((m) => m.type === 'markdown')
    },
    standardMetadata() {
      return this.elementMetadata.filter((m) => m.type !== 'markdown')
    },
    opened() {
      return this.displayStore.dropdowns.metadata ?? true
    },
  },
  methods: {
    fetchMetadata(elementId: UUID) {
      /*
       * This check does not use the computed properties because this might be called with a newValue from a watcher,
       * in which case it is not guaranteed that the computed properties are up to date.
       */
      if (!elementId || this.metadataStore.metadata[elementId]) return
      this.metadataStore.list(elementId)
    },
  },
  watch: {
    opened: {
      immediate: true,
      handler(newValue) {
        if (newValue) this.fetchMetadata(this.elementId)
      },
    },
    elementId: 'fetchMetadata',
  },
})
</script>

<style scoped>
.card-content {
  max-width: 100ch;
}

textarea {
  min-width: 500px;
}
</style>
